import useCatalogUpdateQueue from '../hooks/useCatalogUpdateQueue';
import { fetchCatalogDelta, mergeCatalogDelta } from '../utils/catalogDelta';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import VarietyModal from '../components/VarietyModal';
import CategoryFilterBar from '../components/CategoryFilterBar';
import PosProductCard from '../components/PosProductCard';
import ProductGrid from '../components/ProductGrid';
import ProductSearchInput from '../components/ProductSearchInput';
import { getProducts } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useProductEvents from '../hooks/useProductEvents';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import { applyProductEvent } from '../utils/productEventPatch';
import { areAllPricesSoldOut } from '../utils/productAvailability';
import {
  filterProductSearchResults,
  getProductSearchMatch,
  normalizeProductSearchText,
  productMatchesProductSearch,
} from '../utils/productSearch';
import usePromotionClock from '../hooks/usePromotionClock';
import useCatalogRefreshState from '../hooks/useCatalogRefreshState';
import { collectCatalogPromotions } from '../utils/priceListPromotions';

const normalizeCategoryKey = (product) => {
  const raw = product?.categoryId ?? product?.Category?.id ?? 'uncategorized';
  return String(raw || 'uncategorized');
};

const resolveCategoryLabel = (product) => {
  return (
    product?.Category?.name ||
    product?.categoryName ||
    product?.categoryLabel ||
    (product?.categoryId ? `Categoria ${product.categoryId}` : 'Sin categoria')
  );
};

const getPricedPrices = (variety) =>
  (variety.VarietyPrices || []).filter((price) => price.price !== null && price.price !== undefined);

const hasCatalogPrice = (product) =>
  (product.Varieties || []).some((variety) => getPricedPrices(variety).length > 0);

const isProductSoldOut = (product) =>
  areAllPricesSoldOut((product.Varieties || []).flatMap(getPricedPrices));

const matchesSearch = (product, search, searchMode) => {
  return productMatchesProductSearch(product, search, { searchMode });
};

const compareProductsForSearch = (search, searchMode) => (a, b) => {
  const normalizedSearch = normalizeProductSearchText(search);
  const nameA = normalizeProductSearchText(a.productName);
  const nameB = normalizeProductSearchText(b.productName);
  if (normalizedSearch) {
    const aMatch = getProductSearchMatch(a, search, { searchMode });
    const bMatch = getProductSearchMatch(b, search, { searchMode });
    if (aMatch.exactMatches !== bMatch.exactMatches) return aMatch.exactMatches ? -1 : 1;
    const aExact = nameA === normalizedSearch;
    const bExact = nameB === normalizedSearch;
    if (aExact !== bExact) return aExact ? -1 : 1;
    const aStarts = nameA.startsWith(normalizedSearch);
    const bStarts = nameB.startsWith(normalizedSearch);
    if (aStarts !== bStarts) return aStarts ? -1 : 1;
    if (aStarts && bStarts && nameA.length !== nameB.length) return nameA.length - nameB.length;
  }
  return a.productName.localeCompare(b.productName);
};

const buildCategoryOptions = (productsList) => {
  const options = new Map();
  productsList.forEach((product) => {
    const id = normalizeCategoryKey(product);
    const label = resolveCategoryLabel(product);
    const existing = options.get(id);
    if (!existing) {
      options.set(id, {
        id,
        label,
        initial: String(label || id).trim().charAt(0).toUpperCase(),
      });
      return;
    }
    if (label && existing.label !== label) {
      options.set(id, {
        ...existing,
        label,
        initial: String(label).trim().charAt(0).toUpperCase(),
      });
    }
  });
  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const rebindSelectedCatalogProduct = (selectedProduct, productsList) => {
  if (selectedProduct?.id == null) return null;
  const selectedId = String(selectedProduct.id);
  return (Array.isArray(productsList) ? productsList : []).find(
    (product) => product?.id != null && String(product.id) === selectedId
  ) || null;
};

const ProductGallery = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [promotionClock, setPromotionClock] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const effectiveBranchId = branchId || 1;
  const catalogRefreshScopeKey = String(effectiveBranchId);
  const { searchMode } = useProductSearchSettings();
  const {
    begin: beginCatalogRefresh,
    latest: latestCatalogState,
  } = useCatalogRefreshState(catalogRefreshScopeKey, { branchId: effectiveBranchId, products });
  const catalogPromotions = useMemo(() => collectCatalogPromotions(products), [products]);
  const promotionBoundaries = useMemo(() => (
    promotionClock?.nextBoundaryAt
      ? [...catalogPromotions, { startsAt: promotionClock.nextBoundaryAt }]
      : catalogPromotions
  ), [catalogPromotions, promotionClock?.nextBoundaryAt]);

  const buildFilteredProducts = (productsList, search, category) => {
    const scopedProducts = productsList.filter((product) => {
      if (!hasCatalogPrice(product)) return false;
      if (category !== 'all' && normalizeCategoryKey(product) !== String(category)) return false;
      return true;
    });
    if (!search.trim()) return scopedProducts;
    return filterProductSearchResults(
      scopedProducts.filter((product) => matchesSearch(product, search, searchMode)),
      search,
      { searchMode }
    );
  };

  const refreshProducts = useCallback(async (event) => {
    const request = beginCatalogRefresh(catalogRefreshScopeKey);
    if (!request) return;
    try {
      const data = await fetchCatalogDelta((params) => getProducts(effectiveBranchId, { pageSize: 0, ...params }), event, latestCatalogState(request)?.products || []);
      if (!latestCatalogState(request)) return;
      const normalized = normalizePaginatedResponse(data);
      const productsData = mergeCatalogDelta(latestCatalogState(request)?.products || [], normalized.data || [], data?.deltaProductIds);
      setProducts(productsData);
      setSelectedProduct((current) => rebindSelectedCatalogProduct(current, productsData));
      setPromotionClock(data?.promotionClock || null);
    } catch (error) {
      if (!latestCatalogState(request)) return;
      console.error('Error fetching products:', error);
      if (event) throw error;
    }
  }, [
    beginCatalogRefresh,
    catalogRefreshScopeKey,
    effectiveBranchId,
    latestCatalogState,
  ]);

  const scheduleProductRefresh = useCatalogUpdateQueue(catalogRefreshScopeKey, refreshProducts);
  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      return event?.type === 'products.invalidated' ? scheduleProductRefresh(event) : refreshProducts(event);
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
    setSelectedProduct((current) => rebindSelectedCatalogProduct(current, nextProducts));
  }, [products, scheduleProductRefresh, refreshProducts]);

  useProductEvents(effectiveBranchId, handleProductEvent);

  useEffect(() => {
    setProducts([]);
    setFilteredProducts([]);
    setSelectedProduct(null);
    setPromotionClock(null);
    setSelectedCategory('all');
  }, [catalogRefreshScopeKey]);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const promotionNow = usePromotionClock(
    promotionBoundaries,
    refreshProducts,
    promotionClock
  );

  useEffect(() => {
    setFilteredProducts(buildFilteredProducts(products, searchTerm, selectedCategory));
  }, [products, searchTerm, selectedCategory, searchMode]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    filterProducts(value, selectedCategory);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    filterProducts(searchTerm, category);
  };

  const filterProducts = (search, category) => {
    setFilteredProducts(buildFilteredProducts(products, search, category));
  };

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Lista de Productos</h1>
          </div>

          {/* Search Bar */}
          <div className="sticky top-0 pt-2 z-10 bg-gray-100">
            <ProductSearchInput
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              products={products.filter(hasCatalogPrice)}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="w-full mb-6 p-3 border border-gray-300 rounded-lg shadow focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <CategoryFilterBar
            categories={buildCategoryOptions(products.filter(hasCatalogPrice))}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            itemsWrapperClassName="mb-6"
          />

          {/* Product Grid */}
          <div className="space-y-8">
            {(selectedCategory === 'all'
              ? buildCategoryOptions(filteredProducts)
              : buildCategoryOptions(filteredProducts).filter((category) => String(category.id) === String(selectedCategory))
            ).map((category) => {
              const productsInCategory = filteredProducts
                .filter((product) => normalizeCategoryKey(product) === String(category.id))
                .sort(compareProductsForSearch(searchTerm, searchMode));

              if (productsInCategory.length === 0) return null;

              return (
                <div key={category.id}>
                  {/* Category Title */}
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                      {category.label}
                    </h2>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>

                  {/* Products Grid */}
                  <ProductGrid>
                    {productsInCategory.map((product) => (
                      <PosProductCard
                        key={product.id}
                        productName={product.productName}
                        imageSrc={product.productImage || 'https://placehold.co/200'}
                        badge={isProductSoldOut(product) ? 'Agotado' : ''}
                        onClick={() => setSelectedProduct(product)}
                      />
                    ))}
                  </ProductGrid>
                </div>
              );
            })}
            {filteredProducts.length === 0 && (
              <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">
                No hay productos para los filtros seleccionados.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Varieties */}
      {selectedProduct && (
        <VarietyModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          promotionNow={promotionNow}
        />
      )}
    </>
  );
};

export default ProductGallery;
